import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { FirebaseFirestore } from '@capacitor-firebase/firestore';
import React, { createContext, useContext, useEffect, useState } from 'react';
import logger from '../utils/logger';
import apiClient from '../utils/apiClient';

// Create the context
const FirebaseContext = createContext(null);

// Hook to use the Firebase context
export const useFirebase = () => useContext(FirebaseContext);

// Firebase context provider component
export const FirebaseProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [googleSignInProgress, setGoogleSignInProgress] = useState(false);
    const [userPlan, setUserPlan] = useState(() => localStorage.getItem('userPlan') || 'trial');
    const [planExpiresAt, setPlanExpiresAt] = useState(() => {
        const savedDate = localStorage.getItem('planExpiresAt');
        console.log(`[FirebaseContext-INIT] Reading 'planExpiresAt' from localStorage. Found: ${savedDate}`);
        return savedDate ? new Date(savedDate) : null;
    });
    const [planFeatures, setPlanFeatures] = useState(null); // This is fine, it's always fetched live
    const [subscriptionDetails, setSubscriptionDetails] = useState(null); // Also fine
    const [pendingDowngradeToPlan, setPendingDowngradeToPlan] = useState(() => localStorage.getItem('pendingDowngradeToPlan') || null);
    const [pendingDowngradeAt, setPendingDowngradeAt] = useState(() => {
        const savedDate = localStorage.getItem('pendingDowngradeAt');
        return savedDate ? new Date(savedDate) : null;
    });
  // Listen for auth state changes
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      // Web platform: Use Firebase Web SDK
      logger.info('FirebaseContext - Setting up auth state listener for WEB');
      const { getAuth, onAuthStateChanged, getIdToken } = require('firebase/auth');
      const { auth: webAuth } = require('../utils/firebaseConfig'); // webAuth is the initialized auth instance for web
      const { getFirestore, doc, getDoc, setDoc } = require('firebase/firestore');
      const { db: webDb } = require('../utils/firebaseConfig'); // webDb is the initialized firestore instance for web

      const unsubscribe = onAuthStateChanged(webAuth, async (authUser) => {
        console.log('[FirebaseContext-onAuthStateChanged-WEB] Auth state changed. User:', authUser ? authUser.email : 'null');
        if (authUser) {
          logger.info(`FirebaseContext - User authenticated (Web): ${authUser.email}`);
          setUser(authUser);

          try {
            const token = await getIdToken(authUser);
            localStorage.setItem('access_token', token);
            logger.info('FirebaseContext - User authenticated and token stored (Web)');

            try {
              const userDocRef = doc(webDb, 'users', authUser.uid);
              const userDocSnap = await getDoc(userDocRef);

              if (!userDocSnap.exists()) {
                const userData = {
                  email: authUser.email || '',
                  displayName: authUser.displayName || '',
                  photoURL: authUser.photoURL || '',
                  createdAt: new Date(),
                  lastLoginAt: new Date(),
                  plan: 'trial',
                  planExpiresAt: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000), // 3 days trial
                  emailVerified: authUser.emailVerified || false,
                };
                await setDoc(userDocRef, userData);
                logger.info('FirebaseContext - Created user document in Firestore (Web)');
                setUserPlan('trial');
                setPlanExpiresAt(userData.planExpiresAt);
                localStorage.setItem('userPlan', 'trial');
                localStorage.setItem('planExpiresAt', userData.planExpiresAt.toISOString());
              } else {
                await setDoc(userDocRef, { lastLoginAt: new Date() }, { merge: true });
                const userData = userDocSnap.data();
                if (userData.plan) {
                  setUserPlan(userData.plan);
                  localStorage.setItem('userPlan', userData.plan);
                }
                if (userData.pendingDowngradeToPlan) {
                  setPendingDowngradeToPlan(userData.pendingDowngradeToPlan);
                  localStorage.setItem('pendingDowngradeToPlan', userData.pendingDowngradeToPlan);
                }
                if (userData.pendingDowngradeAt) {
                  const downgradeDate = userData.pendingDowngradeAt instanceof Date
                    ? userData.pendingDowngradeAt
                    : userData.pendingDowngradeAt.toDate();
                  setPendingDowngradeAt(downgradeDate);
                  localStorage.setItem('pendingDowngradeAt', downgradeDate.toISOString());
                }
                if (userData.planExpiresAt) {
                  const expiresAt = userData.planExpiresAt instanceof Date
                    ? userData.planExpiresAt
                    : userData.planExpiresAt.toDate();
                  setPlanExpiresAt(expiresAt);
                  localStorage.setItem('planExpiresAt', expiresAt.toISOString());
                }
                logger.info('FirebaseContext - Updated existing user in Firestore (Web)');
              }
            } catch (firestoreError) {
              logger.error('FirebaseContext - Error handling Firestore (Web):', firestoreError);
            }

            try {
              const profileResponse = await apiClient.get('/api/profile');
              if (profileResponse.status === 200) {
                const userData = await profileResponse.json();
                if (userData.username) {
                  localStorage.setItem('username', userData.username);
                  logger.info('FirebaseContext - Username stored from MongoDB (Web)');
                }
                fetchUserPlan(authUser);
              } else if (profileResponse.status === 401) {
                const username = localStorage.getItem('username') || authUser.displayName || authUser.email.split('@')[0];
                const createResponse = await apiClient.put('/api/profile', { username });
                if (createResponse.ok) {
                  logger.info('FirebaseContext - User created in MongoDB (Web)');
                  const newUserData = await createResponse.json();
                  if (newUserData.username) localStorage.setItem('username', newUserData.username);
                  setUserPlan('trial');
                  localStorage.setItem('userPlan', 'trial');
                  const trialExpiration = new Date();
                  trialExpiration.setDate(trialExpiration.getDate() + 3);
                  setPlanExpiresAt(trialExpiration);
                  localStorage.setItem('planExpiresAt', trialExpiration.toISOString());
                } else {
                  logger.error('FirebaseContext - Failed to create user in MongoDB (Web)');
                }
              }
            } catch (profileError) {
              logger.error('FirebaseContext - Error syncing user with MongoDB (Web):', profileError);
            }
          } catch (err) {
            logger.error('FirebaseContext - Error getting ID token (Web):', err);
          }
        } else {
          logger.info('FirebaseContext - User signed out (Web)');
          setUser(null);
          setUserPlan('trial');
          setPlanExpiresAt(null);
          setPlanFeatures(null);
          setSubscriptionDetails(null);
          localStorage.removeItem('access_token');
          localStorage.removeItem('id_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('userPlan');
          localStorage.removeItem('planExpiresAt');
        }
        setLoading(false);
      }, (authError) => {
        logger.error('FirebaseContext - Auth state error (Web):', authError);
        setError(authError);
        setLoading(false);
      });
      return () => unsubscribe();
    } else {
      // Native platform: Use Capacitor Firebase Authentication
      logger.info('FirebaseContext - Setting up auth state listener for NATIVE');
      let isInitialAuthHandled = false;

      const handleUserLogin = async (nativeUser, idToken) => { // Note: idToken might be passed if available from listener
        if (isInitialAuthHandled) {
            logger.info('FirebaseContext - Initial auth already handled, skipping duplicate call.');
            return;
        }
        isInitialAuthHandled = true;
        try {
          setUser(nativeUser); // uid, email, displayName, photoUrl are available
          // The issue feedback's snippet for handleUserLogin shows idToken being passed as a parameter
          // The FirebaseAuthentication.addListener('authStateChange', callback) typically provides change.user which might have idToken
          // Let's assume nativeUser object might contain idToken, or it's passed separately as per snippet.
          // If nativeUser.idToken is the source, use that. If idToken is passed as a second param, use that.
          // The original snippet used 'nativeUser.idToken'. The new one implies 'idToken' as a param.
          // For now, I will assume nativeUser contains what's needed or idToken is passed if the listener provides it.
          // The FirebaseAuthentication.addListener usually provides 'change.user' which should have the token.
          // Let's stick to the structure from the feedback:
          // if (change.user) { handleUserLogin(change.user, change.user.idToken); }
          // So, idToken would be nativeUser.idToken.
          // The provided snippet has 'if (idToken)' which implies it is correctly passed.

          if (nativeUser.idToken) { // Assuming idToken is part of nativeUser or passed correctly
            localStorage.setItem('access_token', nativeUser.idToken);
            logger.info('FirebaseContext - User authenticated and token stored (Native)');
          } else if (idToken) { // If passed as a separate arg as per the new snippet structure for handleUserLogin call
             localStorage.setItem('access_token', idToken);
             logger.info('FirebaseContext - User authenticated and token stored (Native from param)');
          }


          const userDocRef = `users/${nativeUser.uid}`;
          // The plugin uses 'reference' for path, not 'collection' and 'path' separately for getDocument
          const { doc: userDocSnap } = await FirebaseFirestore.getDocument({ reference: userDocRef });

          if (userDocSnap && userDocSnap.exists) { // Check userDocSnap.exists as per plugin examples
            // --- EXISTING USER LOGIC ---
            // Plugin uses 'reference' and 'data'
            await FirebaseFirestore.updateDocument({ reference: userDocRef, data: { lastLoginAt: new Date().toISOString() } });
            const userData = userDocSnap.data; // data is a method or property depending on plugin version

            // Set plan details from Firestore
            setUserPlan(userData.plan || 'trial');
            localStorage.setItem('userPlan', userData.plan || 'trial');

            // --- START of ADDED LOGIC from feedback ---

            if (userData.pendingDowngradeToPlan) {
                setPendingDowngradeToPlan(userData.pendingDowngradeToPlan);
                localStorage.setItem('pendingDowngradeToPlan', userData.pendingDowngradeToPlan);
            }

            if (userData.pendingDowngradeAt) {
                const downgradeDate = userData.pendingDowngradeAt.seconds
                    ? new Date(userData.pendingDowngradeAt.seconds * 1000)
                    : new Date(userData.pendingDowngradeAt);
                setPendingDowngradeAt(downgradeDate);
                localStorage.setItem('pendingDowngradeAt', downgradeDate.toISOString());
            }

            if (userData.planExpiresAt) {
                const expiresAtDate = userData.planExpiresAt.seconds
                    ? new Date(userData.planExpiresAt.seconds * 1000)
                    : new Date(userData.planExpiresAt);
                setPlanExpiresAt(expiresAtDate);
                // Ensure expiresAtDate is valid before calling toISOString
                if (expiresAtDate && typeof expiresAtDate.toISOString === 'function') {
                    localStorage.setItem('planExpiresAt', expiresAtDate.toISOString());
                } else {
                    // Handle cases where date might be invalid or null
                    localStorage.removeItem('planExpiresAt');
                }
            } else {
                 localStorage.removeItem('planExpiresAt');
                 setPlanExpiresAt(null); // Ensure state is also cleared
            }

            // --- END of ADDED LOGIC from feedback ---
            logger.info('FirebaseContext - Updated existing user in Firestore (Native)');

          } else {
            // --- NEW USER LOGIC ---
            const planExpiresDate = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);
            const newUserDoc = {
              email: nativeUser.email || '',
              displayName: nativeUser.displayName || '',
              photoURL: nativeUser.photoUrl || nativeUser.photoURL || '', // check for both casings
              createdAt: new Date().toISOString(),
              lastLoginAt: new Date().toISOString(),
              plan: 'trial',
              planExpiresAt: planExpiresDate.toISOString(),
              emailVerified: nativeUser.emailVerified || false,
            };
            // Plugin uses 'reference' and 'data'
            await FirebaseFirestore.setDocument({ reference: userDocRef, data: newUserDoc });
            logger.info('FirebaseContext - Created user document in Firestore (Native)');

            setUserPlan('trial');
            setPlanExpiresAt(planExpiresDate);
            localStorage.setItem('userPlan', 'trial');
            localStorage.setItem('planExpiresAt', planExpiresDate.toISOString());
          }

          // --- START of ADDED MONGODB/API SYNC LOGIC from feedback ---
          try {
              const profileResponse = await apiClient.get('/api/profile');
              if (profileResponse.status === 200) {
                const mongoUserData = await profileResponse.json(); // Renamed to avoid conflict with userData from Firestore
                if (mongoUserData.username) {
                  localStorage.setItem('username', mongoUserData.username);
                  logger.info('FirebaseContext - Username stored from MongoDB (Native)');
                }
                fetchUserPlan(nativeUser);
              } else if (profileResponse.status === 401) {
                const username = localStorage.getItem('username') || nativeUser.displayName || (nativeUser.email ? nativeUser.email.split('@')[0] : '');
                const createResponse = await apiClient.put('/api/profile', { username });
                if (createResponse.ok) {
                  logger.info('FirebaseContext - User created in MongoDB (Native)');
                  const newMongoUserData = await createResponse.json(); // Renamed
                  if (newMongoUserData.username) localStorage.setItem('username', newMongoUserData.username);
                } else {
                  logger.error('FirebaseContext - Failed to create user in MongoDB (Native)');
                }
              }
          } catch (profileError) {
              logger.error('FirebaseContext - Error syncing user with MongoDB (Native):', profileError);
          }
          // --- END of ADDED MONGODB/API SYNC LOGIC from feedback ---

        } catch (e) {
          logger.error('FirebaseContext - Error in handleUserLogin (Native):', e);
          setError(e); // Assuming setError is available in this scope
        } finally {
          setLoading(false); // Assuming setLoading is available
        }
      };

      const handleUserLogout = () => {
        isInitialAuthHandled = false;
        logger.info('FirebaseContext - User signed out (Native)');
        setUser(null);
        // ... (Your existing state clearing logic for logout) ...
        setUserPlan('trial');
        setPlanExpiresAt(null);
        setPlanFeatures(null);
        setSubscriptionDetails(null);
        localStorage.clear();
        setLoading(false);
      };

      const listener = FirebaseAuthentication.addListener('authStateChange', (change) => {
        if (change.user) {
          const processLogin = async () => {
            try {
                const { token } = await FirebaseAuthentication.getIdToken(); // Always get fresh token
                handleUserLogin(change.user, token);
            } catch (error) {
                logger.warn('FirebaseContext - Could not get token on authStateChange, likely a transient state.', error);
                handleUserLogout(); // Treat as logout
            }
          };
          processLogin();
        } else {
          handleUserLogout();
        }
      });

      return () => {
        listener.remove();
      };
    }
  }, []);

  // Fetch the user's subscription plan from the server
  const fetchUserPlan = async (currentUser) => { // <-- Accepts an argument
  console.log('[FirebaseContext-fetchUserPlan] ENTERING fetchUserPlan. User:', currentUser ? currentUser.email : 'null');
  if (!currentUser) { // <-- Checks the argument instead of the state
    console.warn('[FirebaseContext-fetchUserPlan] No user provided to fetchUserPlan, cannot fetch plan.');
    return;
  }

    logger.info('FirebaseContext - fetchUserPlan: Attempting to fetch user plan from /api/stripe/subscription');
    console.log('[FirebaseContext-fetchUserPlan] Attempting to fetch from /api/stripe/subscription');
    try {
      const response = await apiClient.get('/api/stripe/subscription');
      
      logger.info(`FirebaseContext - fetchUserPlan: Response status from /api/stripe/subscription: ${response.status}`);

      if (response.ok) {
        const data = await response.json();
        logger.info('FirebaseContext - fetchUserPlan: Successfully fetched data:', data);
        
        // Update state with plan information - preserve 'canceled' status from server
        setUserPlan(data.plan || 'trial');
        setPlanExpiresAt(data.planExpiresAt ? new Date(data.planExpiresAt) : null);
        setPlanFeatures(data.features || null);
        setSubscriptionDetails(data.subscription || null);
        
        // Update pending downgrade information
        if (data.pendingDowngradeToPlan) {
          setPendingDowngradeToPlan(data.pendingDowngradeToPlan);
          setPendingDowngradeAt(data.pendingDowngradeAt ? new Date(data.pendingDowngradeAt) : null);
          
          logger.info('FirebaseContext - fetchUserPlan: Storing pendingDowngradeToPlan in localStorage:', data.pendingDowngradeToPlan);
          localStorage.setItem('pendingDowngradeToPlan', data.pendingDowngradeToPlan);
          if (data.pendingDowngradeAt) {
            logger.info('FirebaseContext - fetchUserPlan: Storing pendingDowngradeAt in localStorage:', data.pendingDowngradeAt);
            localStorage.setItem('pendingDowngradeAt', data.pendingDowngradeAt);
          }
        } else {
          setPendingDowngradeToPlan(null);
          setPendingDowngradeAt(null);
          logger.info('FirebaseContext - fetchUserPlan: Removing pendingDowngradeToPlan and pendingDowngradeAt from localStorage');
          localStorage.removeItem('pendingDowngradeToPlan');
          localStorage.removeItem('pendingDowngradeAt');
        }
        
        // Store in localStorage for offline access
        logger.info('FirebaseContext - fetchUserPlan: Storing userPlan in localStorage:', data.plan || 'trial');
        localStorage.setItem('userPlan', data.plan || 'trial');
        if (data.planExpiresAt) {
          logger.info('FirebaseContext - fetchUserPlan: Storing planExpiresAt in localStorage:', data.planExpiresAt);
          localStorage.setItem('planExpiresAt', data.planExpiresAt);
        }
        
        // Store subscription status
        if (data.subscriptionStatus) {
          localStorage.setItem('subscriptionStatus', data.subscriptionStatus);
        }
        
        logger.info(`FirebaseContext - User plan fetched: ${data.plan}, status: ${data.subscriptionStatus || 'N/A'}`);
      } else {
        logger.error(`FirebaseContext - fetchUserPlan: Failed to fetch plan. Status: ${response.status}`);
        try {
            const errorData = await response.text(); // Try to get error text
            logger.error(`FirebaseContext - fetchUserPlan: Error data: ${errorData}`);
        } catch (e) {
            logger.error('FirebaseContext - fetchUserPlan: Could not parse error response text.');
        }
      }
    } catch (error) {
      logger.error('FirebaseContext - fetchUserPlan: Error during API call to /api/stripe/subscription:', error);
    }
  };

  // Update the user's plan in the database
  const updateUserPlan = async (newPlan) => {
    if (!user) {
      logger.warn('FirebaseContext - No user logged in, cannot update plan');
      return;
    }

    try {
      const response = await apiClient.put('/api/profile', { plan: newPlan });
      
        if (response.ok) {
            // Update local state
            setUserPlan(newPlan);
            localStorage.setItem('userPlan', newPlan);

            // Refresh subscription details
            fetchUserPlan(user); // Pass the user object from the context's state

            logger.info(`FirebaseContext - User plan updated to ${newPlan}`);
            return true;
      } else {
        logger.error('FirebaseContext - Failed to update user plan');
        return false;
      }
    } catch (error) {
      logger.error('FirebaseContext - Error updating user plan:', error);
      return false;
    }
  };

  // Check if the user's plan is active (not expired)
  const isPlanActive = () => {
    if (!planExpiresAt) return userPlan === 'trial'; // Trial is active if no expiration
    
    const now = new Date();
    return now < planExpiresAt;
  };

  // Check if the user has access to a specific feature based on their plan
  const hasFeatureAccess = (featureName) => {
    if (!planFeatures) return false;
    
    if (!isPlanActive()) return false;

    // Early return for some common feature checks
    switch (featureName) {
      case 'chatbot':
        return userPlan === 'premium';
      case 'dictionary':
        return userPlan === 'pro' || userPlan === 'premium';
      case 'audioPlayer':
        return userPlan === 'pro' || userPlan === 'premium';
      default:
        // For other features, check the planFeatures object
        return !!planFeatures[featureName];
    }
  };

  // Cancel the current subscription
  const cancelSubscription = async () => {
    if (!user) {
      logger.warn('FirebaseContext - No user logged in, cannot cancel subscription');
      return false;
    }

    try {
      const response = await apiClient.post('/api/stripe/cancel-subscription');
      
        if (response.ok) {
            // Refresh subscription details to reflect pending cancellation
            fetchUserPlan(user); // Pass the user object here as well

            logger.info('FirebaseContext - Subscription cancelled successfully');
            return true;
      } else {
        logger.error('FirebaseContext - Failed to cancel subscription');
        return false;
      }
    } catch (error) {
      logger.error('FirebaseContext - Error cancelling subscription:', error);
      return false;
    }
  };

  // Create a Stripe customer portal session for managing billing
  const createBillingPortalSession = async () => {
    if (!user) {
      logger.warn('FirebaseContext - No user logged in, cannot access billing portal');
      return null;
    }

    try {
      const response = await apiClient.post('/api/stripe/create-portal-session');
      
      if (response.ok) {
        const data = await response.json();
        logger.info('FirebaseContext - Billing portal session created');
        return data.url;
      } else {
        logger.error('FirebaseContext - Failed to create billing portal session');
        return null;
      }
    } catch (error) {
      logger.error('FirebaseContext - Error creating billing portal session:', error);
      return null;
    }
  };

  // Sign in with email and password
    const signIn = async (email, password) => {
      setLoading(true);
      try {
        if (Capacitor.isNativePlatform()) {
          await FirebaseAuthentication.signInWithEmailAndPassword({ email, password });
          // After signing in, get the token and return it
          const { token } = await FirebaseAuthentication.getIdToken();
          return token;
        } else {
          const { signInWithEmailAndPassword } = require('firebase/auth');
          const { auth } = require('../utils/firebaseConfig');
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          // Get the token from the credential and return it
          return await userCredential.user.getIdToken();
        }
      } catch (err) {
        logger.error('FirebaseContext - Sign in error:', err);
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    };

  // Sign up with email and password
    const signUp = async (email, password) => {
      setLoading(true);
      try {
        if (Capacitor.isNativePlatform()) {
          await FirebaseAuthentication.createUserWithEmailAndPassword({ email, password });
          // The listener will handle the user creation logic in Firestore.
          // We just need to get and return the token for the modal.
        } else {
          const { createUserWithEmailAndPassword } = require('firebase/auth');
          const { auth } = require('../utils/firebaseConfig');
          await createUserWithEmailAndPassword(auth, email, password);
          // The listener will handle the user creation logic in Firestore.
        }

        // After creating the user, get and return their token.
        // Use our own robust getToken function which is platform-aware.
        return await getToken(); // Assuming getToken is defined in this context and is platform-aware

      } catch (err) {
        logger.error('FirebaseContext - Sign up error:', err);
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    };

  // Sign in with Google
    const signInWithGoogle = async () => {
      setLoading(true);
      setGoogleSignInProgress(true);
      try {
        if (Capacitor.isNativePlatform()) {
          await FirebaseAuthentication.signInWithGoogle();
          // The listener will handle the Firestore logic.
          // Get the token and return it.
          const { token } = await FirebaseAuthentication.getIdToken();
          return token;
        } else {
          const { GoogleAuthProvider, signInWithPopup } = require('firebase/auth');
          const { auth } = require('../utils/firebaseConfig');
          const provider = new GoogleAuthProvider();
          const userCredential = await signInWithPopup(auth, provider);
          // The listener will handle the Firestore logic.
          // Get the token from the credential and return it.
          return await userCredential.user.getIdToken();
        }
      } catch (err) {
        logger.error('FirebaseContext - Google sign in error:', err);
        setError(err);
        throw err;
      } finally {
        setGoogleSignInProgress(false);
        setLoading(false);
      }
    };

  // Sign out
  const logOut = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        await FirebaseAuthentication.signOut();
        // For native, local storage clearing might need to be more explicit
        // if authStateChange listener doesn't handle it comprehensively for logout.
        // However, the issue description implies the authStateChange listener handles UI updates.
        // Let's stick to the provided snippet for now.
      } else {
        const { signOut } = require('firebase/auth');
        const { auth } = require('../utils/firebaseConfig');
        await signOut(auth);
      }
      // Common local storage clearing, ensure this is still desired
      // The original logOut function had specific localStorage.removeItem calls.
      // The new useEffect's onAuthStateChanged for web, and handleUserLogout for native
      // should take care of user state and related localStorage items.
      // Let's review if any specific items need to be cleared here additionally,
      // beyond what the auth listeners do. The issue description implies the new
      // useEffect handles this. For now, I will retain the original local storage clearing
      // to be safe, as it was present in the original `logOut` and not explicitly
      // removed by the issue description for this function.
      localStorage.removeItem('access_token');
      localStorage.removeItem('id_token'); // If you use it
      localStorage.removeItem('refresh_token'); // If you use it
      localStorage.removeItem('userId'); // Or 'user_id'
      localStorage.removeItem('username');
      localStorage.removeItem('userProfile'); // Or similar
      localStorage.removeItem('isAdmin'); // If you use it
      localStorage.removeItem('userPlan');
      localStorage.removeItem('planExpiresAt');
      logger.info('FirebaseContext - User signed out successfully'); // Moved logger call after operations
    } catch (err) {
      logger.error('FirebaseContext - Sign out error:', err);
      setError(err);
      throw err;
    }
  };

  // Get current user's ID token
  const getToken = async (forceRefresh = false) => {
    if (!user) {
      logger.warn('FirebaseContext - No user logged in');
      throw new Error('Authentication required');
    }

    try {
      if (Capacitor.isNativePlatform()) {
        // NATIVE PATH: Use the plugin to get the token
        const { token } = await FirebaseAuthentication.getIdToken({ forceRefresh });
        localStorage.setItem('access_token', token);
        logger.debug('FirebaseContext - Token retrieved successfully (Native)');
        return token;
      } else {
        // WEB PATH: Use the web SDK to get the token
        const { getIdToken } = require('firebase/auth');
        const token = await getIdToken(user, forceRefresh);
        localStorage.setItem('access_token', token);
        logger.debug('FirebaseContext - Token retrieved successfully (Web)');
        return token;
      }
    } catch (err) {
      logger.error('FirebaseContext - Error getting token:', err);
      throw err; // Removed setError(err) as it's not in the latest snippet for this fn
    }
  };

  // Get user ID from current user or token
  const getUserId = async () => {
    // First try to get it directly from the user object
    if (user && user.uid) {
      return user.uid;
    }
    
    // If no user object, try to extract from token
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        // Decode JWT token
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        
        const decodedToken = JSON.parse(jsonPayload);
        if (decodedToken.sub) {
          return decodedToken.sub;
        }
      }
    } catch (err) {
      logger.error('FirebaseContext - Error extracting user ID from token:', err);
    }
    
    logger.warn('FirebaseContext - No user ID available');
    throw new Error('No user ID available');
  };

  // Update password
  const updateUserPassword = async (oldPassword, newPassword) => {
    if (!user) {
      logger.warn('FirebaseContext - No user logged in');
      throw new Error('Authentication required');
    }

    try {
      if (Capacitor.isNativePlatform()) {
        // You may need to have the user re-authenticate before this is allowed
        // The latest snippet simplifies this by directly calling updatePassword.
        // If re-auth is needed, the plugin might throw an error that needs handling,
        // or a separate re-auth call would be added here.
        await FirebaseAuthentication.updatePassword({ password: newPassword });
        logger.info('FirebaseContext - Password updated successfully (Native)');
      } else {
        const { getAuth, reauthenticateWithCredential, updatePassword: webUpdatePassword, EmailAuthProvider } = require('firebase/auth');
        const auth = getAuth(); // Call getAuth()
        const currentUser = auth.currentUser;
        if (!currentUser) {
          logger.error('FirebaseContext - updateUserPassword (Web) - No current user found in auth state.');
          throw new Error("Authentication session expired. Please sign in again.");
        }
        const credential = EmailAuthProvider.credential(currentUser.email, oldPassword);
        await reauthenticateWithCredential(currentUser, credential);
        await webUpdatePassword(currentUser, newPassword); // Use aliased import
        logger.info('FirebaseContext - Password updated successfully (Web)');
      }
      return true;
    } catch (err) {
      logger.error('FirebaseContext - Error updating password:', err);
      setError(err); // setError(err) is present in this snippet
      throw err;
    }
  };

  // Delete account
  const deleteUserAccount = async (password) => {
    if (!user) {
      logger.warn('FirebaseContext - No user logged in');
      throw new Error('Authentication required');
    }

    try {
      // Step 1: Delete from your MongoDB backend (this is platform-agnostic)
      try {
        const response = await apiClient.delete('/api/profile');
        if (response.ok) {
          logger.info('FirebaseContext - User profile deleted from MongoDB');
        } else {
          // The latest snippet doesn't include response.text() here, just logs status.
          logger.error(`FirebaseContext - Error deleting user from MongoDB: ${response.status}`);
        }
      } catch (dbError) {
        logger.error('FirebaseContext - Error deleting user from MongoDB:', dbError);
      }

      // Step 2: Delete from Firebase Auth
      if (Capacitor.isNativePlatform()) {
        await FirebaseAuthentication.deleteUser();
        logger.info('FirebaseContext - User account deleted successfully from Firebase (Native)');
      } else {
        const { getAuth, deleteUser: webDeleteUser } = require('firebase/auth'); // Aliased webDeleteUser
        const auth = getAuth(); // Call getAuth()
        const currentUser = auth.currentUser;
        if (!currentUser) {
          logger.error('FirebaseContext - deleteUserAccount (Web) - No current user found in auth state.');
          throw new Error("Authentication session expired. Please sign in again.");
        }
        await webDeleteUser(currentUser); // user object from context
        logger.info('FirebaseContext - User account deleted successfully from Firebase (Web)');
      }

      return true;
    } catch (err) {
      logger.error('FirebaseContext - Error in delete account flow:', err);
      setError(err); // setError(err) is present in this snippet
      throw err;
    }
  };

  // This effect runs once and hooks up our unified getToken function to the apiClient.
  useEffect(() => {
    // Make sure we have a valid function to provide.
    if (getToken) {
      apiClient.setAuthTokenProvider(getToken);
    }
  }, [getToken]); // This dependency array ensures it runs if getToken ever changes.

  const value = {
    user,
    loading,
    error,
    googleSignInProgress,
    signIn,
    signUp,
    signInWithGoogle,
    logOut,
    getToken,
    getUserId,
    updateUserPassword,
    deleteUserAccount,
    isAuthenticated: !!user,
    // Plan management
    userPlan,
    planExpiresAt,
    pendingDowngradeToPlan,
    pendingDowngradeAt,
    planFeatures,
    subscriptionDetails,
    fetchUserPlan,
    updateUserPlan,
    isPlanActive,
    hasFeatureAccess,
    cancelSubscription,
    createBillingPortalSession
  };

  return (
    <FirebaseContext.Provider value={value}>
      {children}
    </FirebaseContext.Provider>
  );
};

export default FirebaseContext; 